import styled from 'styled-components';
import AccordionItem from '@mlb/react-patterns/v4/components/AccordionItem/AccordionItem';
import { SubHeader, HeadLine } from '@mlb/react-patterns/v4/components/AccordionItem/AccordionItem.style';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import COLORS from '@mlb/react-patterns/v4/style/universal.color.ts';
import GDBREAKPOINTS from '../themes/breakpoints';

export const DFV_BG_COLOR_CHANNELS = { // srgb color space (chrome - everything else)
    r: 49,
    g: 63,
    b: 79,
    a: 1
};

// the above in rec2020 color space via color func: color(rec2020 0.14801 0.1749 0.23141);
//
export const DFV_BG_COLOR_CHANNELS_ALT = { // bt.709 color space (safari... why?)
    r: 52,
    g: 70,
    b: 86,
    a: 1
};

// #2D3E50 - this was the color from design? Matches ff, and chrome visually, but not numerically rgb from the canvas sample
// #303E4E - this is the color used in the BG in the DFV source canvas (rgb(49, 63, 79))
// #2F3D4C - chrome canvas thinks the bg is this (rgb(47, 61, 76))
// #344656 - safari/ff canvas thinks the bg is this (rgb(52, 70, 86))

export const DFV_BG_COLOR = `rgba(${DFV_BG_COLOR_CHANNELS.r}, ${DFV_BG_COLOR_CHANNELS.g}, ${DFV_BG_COLOR_CHANNELS.b}, ${DFV_BG_COLOR_CHANNELS.a})`;

const { mlbWhite } = COLORS;
const { SM_MAX, MD_MIN } = GDBREAKPOINTS;

/**
 * MATH on the percentages
 * 8% h on 16:9, is 4.5% w.
 */
const DFV_MASK = `
    mask-mode: alpha;
    mask-image:
        linear-gradient(to top, transparent 0%,#000 8% 92%,transparent 100%),
        linear-gradient(to left,transparent 0%,#000 4.5% 95.5%,transparent 100%);
    mask-size: 100% 100%;
    mask-position: center;
    mask-repeat: no-repeat;
    mask-composite: intersect;
`;

export const DynamicFieldViewWrapper = styled.div`
    overflow: hidden;
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 0;
    background-color: ${DFV_BG_COLOR};
    box-sizing: border-box;
`;

export const DynamicFieldViewCanvas = styled.canvas`
    width: 100%;
    aspect-ratio: 16/9;

    ${DFV_MASK}
    @media (min-width: ${MD_MIN}) {
        top: 4px;
        position: relative;
    }

    &[data-gamma-correct='true'] {
        filter: brightness(0.899) contrast(1.02);  // slightly less poor man's gamm
    }
`;

export const DFVVideo = styled.video`
    box-sizing: border-box;
    width: 100%;
    aspect-ratio: 16/9;
    pointer-events: none;
    position: relative;
    z-index: 0;
    object-fit: contain;
    ${DFV_MASK}
    @media (min-width: ${MD_MIN}) {
        top: 4px;
        position: relative;
    }
    ${({ $needsGammaAdjust, hidden }) => $needsGammaAdjust && !hidden ? 'filter: brightness(0.899) contrast(1.02);' : ''}  // slightly less poor man's gamma
`;

export const AccordionWrapper = styled(AccordionItem)`
    height: 48px;
    border-bottom: 1px solid ${({ isOpen }) => isOpen ? 'transparent' : 'var(--gameday-divider-color)'};
    ${HeadLine} {
        color: var(--gameday-primary-font-color);
    }
    ${SubHeader} {
        color: var(--gameday-accordion-indicator-color);
        border: 1px solid var(--gameday-accordion-indicator-color);
        border-radius: 18px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 700;
        padding: 4px 8px ;
        text-align: center;
    }
`;

export const SkeletonWrapper = styled(Skeleton)`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
`;

export const BetaTag = styled.span`
    position: absolute;
    bottom: 18px;
    right: 18px;
    text-transform: uppercase;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
    padding: 3px 9px;
    font-size: 12px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.25);
    user-select: none;
    z-index: 1;
    @media (max-width: ${SM_MAX}) {
        top: 22px;
        bottom: auto;
    }
`;

export const BetaTagLive = styled(BetaTag)`
    top:16px;
    right: 16px;
    @media (max-width: ${SM_MAX}) {
        top:16px;
        right: 16px;
    }
`;

export const VenueLabel = styled.span`
    position: absolute;
    bottom: 16px;
    color: ${mlbWhite};
    font-size: 15px;
    letter-spacing: 0.2px;
    font-weight: 600;
`;

export const DFVWrap = styled.div`
    position: ${({ $isCanvas }) => $isCanvas ? 'absolute' : 'relative'};
    height: 100%;
    width: 100%;
    background: transparent;
    z-index: 0;
    display: flex;
    flex: 0 0 fit-content;
`;

export const DynamicFieldViewLiveWrapper = styled.div`
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    min-height: 0;

    ${DFVWrap} {
      flex: 1;
    }

    ${DFVVideo} {
        height: auto;
        width: 100%;
    }
`;
